import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import {
  TrendingUp,
  Flag,
  AutoGraph,
  EmojiEvents,
  GpsFixed,
  Speed,
  ReportProblem,
  IosShare,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import {
  PageHeader,
  StatCard,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { useChartTokens, axisProps, gridProps, tooltipStyle } from '../../dashboard/chartTokens';
import { formatIndianNumber } from '../../utils/currency';
import crmService, { CrmCommandCenter, CrmForecast } from '../../services/crmService';
import salesAnalyticsService from '../../services/salesAnalyticsService';
import { SalesPipeline, SalesReps } from '../../types/salesAnalytics';

/**
 * Sales Forecast — reads the same forecast block the Revenue Command Center
 * shows (command-center.forecast), then layers on the per-stage and per-rep
 * breakdowns from sales-analytics when those endpoints answer. There is no
 * dedicated "forecast" endpoint on the backend, so this page is a read-only
 * recombination of two services, not a new data source.
 */

const isNum = (v: any): v is number => v !== null && v !== undefined && !isNaN(Number(v));
const fmtMoney = (v: any) => (isNum(v) ? formatIndianNumber(Number(v)) : '—');
const fmtPct = (v: any) => (isNum(v) ? `${Number(v).toFixed(1)}%` : '—');

const SalesForecastPage: React.FC = () => {
  const T = useChartTokens();

  const [cc, setCc] = useState<CrmCommandCenter | null>(null);
  const [ccError, setCcError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Secondary breakdowns — optional. A failure here never blocks the page;
  // the section simply doesn't render (no fabricated rows to fill the gap).
  const [pipeline, setPipeline] = useState<SalesPipeline | null>(null);
  const [reps, setReps] = useState<SalesReps | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setCcError(null);
    crmService
      .getCommandCenter()
      .then(setCc)
      .catch((e: any) => setCcError(e?.message || 'Failed to load the forecast.'))
      .finally(() => setLoading(false));

    salesAnalyticsService
      .getPipeline({})
      .then(setPipeline)
      .catch(() => setPipeline(null));

    salesAnalyticsService
      .getReps({})
      .then(setReps)
      .catch(() => setReps(null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const forecast: CrmForecast | undefined = cc?.forecast;

  const kpiDefs = useMemo(
    () => [
      { key: 'weightedPipeline', label: 'Weighted Pipeline', icon: <TrendingUp />, color: T.series(0) },
      { key: 'commit', label: 'Committed', icon: <Flag />, color: T.status.good },
      { key: 'bestCase', label: 'Best Case', icon: <AutoGraph />, color: T.series(3) },
      { key: 'won', label: 'Won', icon: <EmojiEvents />, color: T.status.good },
      { key: 'target', label: 'Target', icon: <GpsFixed />, color: T.series(6) },
      {
        key: 'coveragePct',
        label: 'Coverage',
        icon: <Speed />,
        color:
          forecast?.coveragePct != null
            ? Number(forecast.coveragePct) >= 100
              ? T.status.good
              : T.status.critical
            : T.textSecondary,
      },
      {
        key: 'gap',
        label: 'Gap to Target',
        icon: <ReportProblem />,
        color: forecast?.gap != null ? (Number(forecast.gap) > 0 ? T.status.critical : T.status.good) : T.textSecondary,
      },
    ],
    [T, forecast]
  );

  const fmtFor = (key: string, v: any) => (key === 'coveragePct' ? fmtPct(v) : fmtMoney(v));

  const forecastBars = useMemo(
    () =>
      forecast
        ? [
            { name: 'Closed-Won', value: Number(forecast.won) || 0 },
            { name: 'Commit', value: Number(forecast.commit) || 0 },
            { name: 'Best-Case', value: Number(forecast.bestCase) || 0 },
            { name: 'Pipeline', value: Number(forecast.pipeline) || 0 },
          ]
        : [],
    [forecast]
  );

  const coverageNarrative = useMemo(() => {
    if (!forecast || forecast.target === null || forecast.target === undefined || Number(forecast.target) === 0) {
      return null;
    }
    const ratio = Number(forecast.weightedPipeline) / Number(forecast.target);
    const verdict = ratio >= 1 ? 'on pace to cover' : 'short of covering';
    return `Weighted pipeline of ${formatIndianNumber(Number(forecast.weightedPipeline))} is ${ratio.toFixed(
      1
    )}× target — ${verdict} the ${formatIndianNumber(Number(forecast.target))} target for this period.`;
  }, [forecast]);

  const byStageData = pipeline?.byStage ?? [];
  const repRows = reps?.reps ?? [];

  return (
    <Box>
      <PageHeader
        title="Sales Forecast"
        subtitle="Committed, best-case and pipeline value against target — this period"
      />

      {loading && !cc ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={28} />
        </Box>
      ) : ccError && !cc ? (
        <ErrorState message={ccError} onRetry={load} />
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                md: 'repeat(auto-fit, minmax(160px, 1fr))',
              },
              gap: 2,
              mb: 2.5,
            }}
          >
            {kpiDefs.map((k) => (
              <StatCard
                key={k.key}
                title={k.label}
                value={fmtFor(k.key, (forecast as any)?.[k.key])}
                icon={k.icon}
                color={k.color}
              />
            ))}
          </Box>

          {coverageNarrative && (
            <Paper
              elevation={0}
              sx={{ p: 1.75, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IosShare sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {coverageNarrative}
                </Typography>
              </Box>
            </Paper>
          )}

          {/* Forecast vs target */}
          <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
              Forecast vs Target
            </Typography>
            {forecastBars.length ? (
              <Box sx={{ width: '100%', height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={forecastBars} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid {...gridProps(T)} />
                    <XAxis dataKey="name" {...axisProps(T)} />
                    <YAxis {...axisProps(T)} tickFormatter={(v: number) => formatIndianNumber(v)} width={72} />
                    <Tooltip {...tooltipStyle(T)} formatter={(v: any) => formatIndianNumber(Number(v))} />
                    {forecast?.target != null && (
                      <ReferenceLine
                        y={Number(forecast.target)}
                        stroke={T.status.warning}
                        strokeDasharray="4 4"
                        label={{ value: 'Target', position: 'right', fill: T.textSecondary, fontSize: 11 }}
                      />
                    )}
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {forecastBars.map((_, i) => (
                        <Cell key={i} fill={T.series(i)} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <TableEmptyState title="No forecast data yet" hint="Forecast populates once opportunities exist." />
            )}
          </Paper>

          {/* Pipeline by stage */}
          {pipeline && (
            <Paper elevation={0} sx={{ p: 2.5, mb: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                Pipeline by Stage
              </Typography>
              {byStageData.length ? (
                <Box sx={{ width: '100%', height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={byStageData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                      <CartesianGrid {...gridProps(T)} />
                      <XAxis dataKey="stage" {...axisProps(T)} />
                      <YAxis {...axisProps(T)} tickFormatter={(v: number) => formatIndianNumber(v)} width={72} />
                      <Tooltip {...tooltipStyle(T)} formatter={(v: any) => formatIndianNumber(Number(v))} />
                      <Legend
                        wrapperStyle={{ fontSize: 12, color: T.textSecondary }}
                        formatter={(v: string) => (v === 'totalValue' ? 'Open Value' : 'Weighted Value')}
                      />
                      <Bar dataKey="totalValue" name="totalValue" fill={T.series(0)} radius={[4, 4, 0, 0]} />
                      <Bar dataKey="weightedValue" name="weightedValue" fill={T.series(1)} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <TableEmptyState title="No open pipeline" hint="Stages populate once opportunities are open." />
              )}
            </Paper>
          )}

          {/* By rep */}
          {reps && (
            <TableSurface>
              <Box sx={{ p: 2.5, pb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  By Sales Rep
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {['Rep', 'Won Value', 'Win Rate', 'Open Pipeline', 'Quota Attainment'].map((h, i) => (
                        <TableCell key={h} align={i === 0 ? 'left' : 'right'} sx={headerCellSx}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {repRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ border: 0 }}>
                          <TableEmptyState title="No rep performance data" hint="Populates once deals are assigned and closed." />
                        </TableCell>
                      </TableRow>
                    ) : (
                      repRows.map((r) => (
                        <TableRow key={r.userId} sx={bodyRowSx}>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {r.fullName || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">{fmtMoney(r.wonValue)}</TableCell>
                          <TableCell align="right">{fmtPct(r.winRatePct)}</TableCell>
                          <TableCell align="right">{fmtMoney(r.openPipelineValue)}</TableCell>
                          <TableCell align="right">{fmtPct(r.attainmentPct)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TableSurface>
          )}
        </>
      )}
    </Box>
  );
};

export default SalesForecastPage;
